import { useCallback, useEffect, useRef, useState } from 'react'
import { getWebinar } from '@domain/features/webinars/webinars-feature'
import type { Webinar } from '@domain/models/webinar'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'
import {
  detailEntityMatchesRoute,
  isDetailLoadErrorFatal,
  keepDetailEntityForRoute,
} from './detail-load-state'

function matchesWebinarRoute(webinar: Webinar, webinarId: string): boolean {
  return webinar.uid === webinarId
}

export function useWebinar(webinarId: string | undefined) {
  const ssrWebinar = useSsrPageData()?.webinarDetail?.webinar ?? null
  const hasMatchingSsrWebinar = detailEntityMatchesRoute(
    ssrWebinar,
    webinarId,
    matchesWebinarRoute,
  )
  const [webinar, setWebinarState] = useState<Webinar | null>(() =>
    keepDetailEntityForRoute(ssrWebinar, webinarId, matchesWebinarRoute))
  const [loading, setLoading] = useState(!hasMatchingSsrWebinar)
  const [error, setError] = useState<string | null>(null)
  const webinarRef = useRef<Webinar | null>(webinar)

  const setWebinar = useCallback((nextWebinar: Webinar | null) => {
    webinarRef.current = nextWebinar
    setWebinarState(nextWebinar)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function load() {
      await Promise.resolve()
      if (cancelled) {
        return
      }

      if (!webinarId) {
        setWebinar(null)
        setError(null)
        setLoading(false)
        return
      }
      const resolvedWebinarId = webinarId
      const ssrFallback = keepDetailEntityForRoute(
        ssrWebinar,
        resolvedWebinarId,
        matchesWebinarRoute,
      )
      if (ssrFallback && !detailEntityMatchesRoute(
        webinarRef.current,
        resolvedWebinarId,
        matchesWebinarRoute,
      )) {
        setWebinar(ssrFallback)
      } else if (!detailEntityMatchesRoute(
        webinarRef.current,
        resolvedWebinarId,
        matchesWebinarRoute,
      )) {
        setWebinar(null)
      }

      try {
        if (!detailEntityMatchesRoute(
          webinarRef.current,
          resolvedWebinarId,
          matchesWebinarRoute,
        )) {
          setLoading(true)
        }
        setError(null)
        const result = await getWebinar(resolvedWebinarId)
        if (!cancelled) {
          setWebinar(result)
        }
      } catch (loadError) {
        if (!cancelled && isDetailLoadErrorFatal(
          webinarRef.current,
          resolvedWebinarId,
          matchesWebinarRoute,
        )) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load webinar.',
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [setWebinar, ssrWebinar, webinarId])

  return { webinar, loading, error }
}
